import React, { useEffect, memo, useState, useCallback } from 'react';
import { Flex, Button, Icon } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { ChevronLeft } from '@kula-ai/icons/outline/ChevronLeft';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { components } from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import moment from 'moment-timezone';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';

// Custom styles for the select component
const customStyles = {
  menu: (provided) => ({
    ...provided,
    width: '450px',
    right: '-75px',
    zIndex: 9,
  }),
};

// Custom option component to format the label
const CustomOption = memo((props) => {
  // @ts-expect-error -- pre-existing type debt (extraction move)
  const { data, isSelected } = props || {};
  const { label } = data || {};

  const [liveTime, setLiveTime] = useState('');

  useEffect(() => {
    // Update time every minute
    const updateTime = () => {
      const time = moment().tz(data.value).format('hh:mm a');
      setLiveTime(time);
    };

    updateTime(); // Initial call
    const interval = setInterval(updateTime, 1000 * 60);

    return () => clearInterval(interval);
  }, [data.value]);

  return (
    <components.Option {...props}>
      <Flex justifyContent="space-between" w="100%" gap="20px">
        <KText maxW={`calc(100% - ${isSelected ? 40 : 20}px)`}>{label}</KText>
        <Flex gap="6px" alignItems="center" flexShrink={0}>
          <KText>{liveTime}</KText>
          {isSelected && (
            <Icon w="18px" h="18px" as={CheckCircle} color="primary-500" />
          )}
        </Flex>
      </Flex>
    </components.Option>
  );
});
CustomOption.displayName = 'CustomOption';

const Title = ({ calendarRef }) => {
  const viewTitle = calendarRef?.current?.calendar?.currentData?.viewTitle;

  return (
    <KText variant="heading_2" ml="6px" lineHeight="29.97px">
      {viewTitle}
    </KText>
  );
};

const TodayButton = ({ calendarRef, registerForUpdates }) => {
  const [isCurrentWeek, setIsCurrentWeek] = useState(false);

  useEffect(() => {
    const unregisterDatesSet = registerForUpdates('datesSet', (data) => {
      const { timeZone } = data?.fullData ?? {};
      if (!calendarRef.current) {
        return;
      }

      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;

      if (view.type !== 'dayGridWeek' && view.type !== 'timeGridWeek') {
        setIsCurrentWeek(false);
        return;
      }

      const viewStart = moment.tz(view.activeStart, timeZone);
      const viewEnd = moment.tz(view.activeEnd, timeZone);
      const today = moment.tz(timeZone);

      // Check if today falls within the view range
      const isTodayInView = today.isBetween(viewStart, viewEnd, 'day', '[]'); // Inclusive range

      setIsCurrentWeek(isTodayInView);
    });

    return () => {
      unregisterDatesSet();
    };
  }, [calendarRef, registerForUpdates]);

  // Handle today button click in a safe way
  const handleTodayClick = useCallback(() => {
    // Use setTimeout to move this operation out of React's rendering cycle
    setTimeout(() => {
      const calendarApi = calendarRef?.current?.getApi();
      if (calendarApi) {
        calendarApi.today();
      }
    }, 0);
  }, [calendarRef]);

  return (
    <Button
      variant={isCurrentWeek ? 'tertiary' : 'quaternary'}
      w="66px"
      h="34px"
      boxShadow="none"
      onClick={handleTodayClick}
    >
      Today
    </Button>
  );
};

export const SchCalToolbar = ({
  calendarRef,
  registerForUpdates,
  setTimezone,
  initialTimezone,
}) => {
  const [selectedTimezone, setSelectedTimezone] = useState(null);

  const calendarApiState = calendarRef?.current?.getApi();

  const { data: { time_zones = {} } = {}, isLoading } = useQuery({
    queryKey: ['getTimeZones'],
    queryFn: () => InterviewDetailsAPI?.getTimeZones(),
  });

  const currUser = useSelector(selectCurrUser);
  const { time_zone: defaultTimezoneFromAPI } = currUser || {};

  const timeZoneOptions = React.useMemo(() => {
    return Object.entries(time_zones || {}).map(([key]) => {
      const { fullName, shortName } = TIME_ZONE_CONSTANTS[key] || {};
      // Calculate current offset using moment-timezone
      const offsetInMinutes = moment().tz(key).utcOffset();
      const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
      const minutes = Math.abs(offsetInMinutes) % 60;
      const sign = offsetInMinutes < 0 ? '-' : '+';
      const liveOffset = `GMT${sign}${String(hours).padStart(2, '0')}:${String(
        minutes
      ).padStart(2, '0')}`;

      return {
        label: `${fullName} (${liveOffset}) - ${shortName}`,
        value: key,
        offset: liveOffset,
      };
    });
  }, [time_zones]);

  // Safe timezone change handler
  const handleTimezoneChange = useCallback(
    (selectedOption) => {
      setSelectedTimezone(selectedOption);
      setTimezone(selectedOption.value);

      // Use setTimeout to move this operation out of React's rendering cycle
      setTimeout(() => {
        if (calendarApiState) {
          calendarApiState.setOption('timeZone', selectedOption.value);
          calendarApiState.refetchEvents();
        }
      }, 0);
    },
    [calendarApiState, setTimezone]
  );

  // Set initial timezone safely with priority: timezone prop > user's stored timezone
  useEffect(() => {
    const preferredTimezone = initialTimezone ?? defaultTimezoneFromAPI;

    if (
      !isLoading &&
      timeZoneOptions.length > 0 &&
      calendarRef &&
      preferredTimezone
    ) {
      const calendarApiState = calendarRef?.current?.getApi();

      // First preference: initialTimezone prop (from Schedule All flow)
      // Second preference: user's stored timezone from API

      const timezoneOption = timeZoneOptions.find(
        (tz) => tz.value === preferredTimezone
      );

      if (timezoneOption) {
        setSelectedTimezone(timezoneOption);
        setTimezone(timezoneOption.value);

        // Use setTimeout to move this operation out of React's rendering cycle
        setTimeout(() => {
          calendarApiState.setOption('timeZone', timezoneOption.value);
        }, 0);
      }
    }
  }, [
    timeZoneOptions,
    isLoading,
    initialTimezone,
    defaultTimezoneFromAPI,
    setTimezone,
    calendarRef,
  ]);

  // Handle navigation safely
  const handlePrev = useCallback(() => {
    setTimeout(() => {
      if (calendarApiState) {
        calendarApiState.prev();
      }
    }, 0);
  }, [calendarApiState]);

  const handleNext = useCallback(() => {
    setTimeout(() => {
      if (calendarApiState) {
        calendarApiState.next();
      }
    }, 0);
  }, [calendarApiState]);

  const renderWeekNavigation = () => {
    return (
      <Flex alignItems="center" w="72px" h="34px">
        <Flex
          alignItems="center"
          justifyContent="center"
          onClick={handlePrev}
          _hover={{ cursor: 'pointer', bg: 'primary-25' }}
          border="1px solid"
          borderRight="none"
          borderColor="border-color"
          borderLeftRadius="sm"
          w="36px"
          h="34px"
        >
          <Icon as={ChevronLeft} color="secondary-700" w="16px" h="16px" />
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          onClick={handleNext}
          _hover={{ cursor: 'pointer', bg: 'primary-25' }}
          border="1px solid"
          borderColor="border-color"
          borderRightRadius="sm"
          w="36px"
          h="34px"
        >
          <Icon as={ChevronRight} color="secondary-700" w="16px" h="16px" />
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex
      minH="60px"
      alignItems="center"
      p="0px 10px"
      borderRight="1px solid"
      borderColor="border-color"
      justifyContent="space-between"
      position="relative"
      zIndex={11}
    >
      {/* left section */}
      <Flex gap="6px" alignItems="center" flexShrink={0} minH="34px">
        {renderWeekNavigation()}
        <TodayButton
          calendarRef={calendarRef}
          registerForUpdates={registerForUpdates}
        />
        <Title calendarRef={calendarRef} />
      </Flex>

      {/* right section */}
      <Flex gap="6px" alignItems="center" maxW="300px" flex={1}>
        <KSelect
          customStyles={customStyles}
          components={{ Option: CustomOption, IndicatorSeparator: null }}
          value={selectedTimezone}
          isLoading={isLoading}
          onChange={handleTimezoneChange}
          options={timeZoneOptions}
          placeholder="Select time zone"
        ></KSelect>
      </Flex>
    </Flex>
  );
};
